import { useEffect, useMemo, useRef, useState } from 'react';

import { toast } from 'sonner';

import { defaultCountingKey, mapCountingToEvent, mapDeltaTypeToCounting } from './procedural-helpers';
import type { ProceduralTimelineEvent } from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event.entity';
import { CreateProceduralEventInput } from '@/api/mutations/useCreateProceduralEvent';
import { Icon } from '@/new/components/icon';
import { SearchableCombobox } from '@/new/components/searchable-combobox';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Switch } from '@/new/components/shadcn/switch';
import { Textarea } from '@/new/components/shadcn/textarea';
import {
  combineDateAndTime,
  instantToCivilUtc,
  splitDateAndTime,
} from '@/new/components/timeline-calculator/civil-date';
import { CivilDatePicker } from '@/new/components/timeline-calculator/civil-date-picker';
import { CountingReference } from '@/new/components/timeline-calculator/counting-reference';
import { EmbeddedCalculator } from '@/new/components/timeline-calculator/embedded-calculator';
import { detectTimeZone, getTimeZoneOptions } from '@/new/lib/time-zones';
import { COUNTING_OPTIONS, CountingKey } from '@/new/stores/timeline-calculator-store';

const DAYS_LIMIT = 365;

/**
 * Normalised form state for a procedural event. Shared by the create/edit
 * dialog and the review-mode suggestion editor so both render identical inputs
 * and validation. Convert to/from the wire types with the helpers below.
 */
export interface ProceduralEventFormValues {
  name: string;
  description: string;
  timeZone: string;
  startDate: Date | null;
  startTime: string;
  endDate: Date | null;
  endTime: string;
  anchorEventId: string;
  deltaDays: string;
  countingKey: CountingKey;
  isConfirmed: boolean;
}

export function emptyEventFormValues(): ProceduralEventFormValues {
  return {
    name: '',
    description: '',
    timeZone: detectTimeZone(),
    startDate: null,
    startTime: '',
    endDate: null,
    endTime: '',
    anchorEventId: '',
    deltaDays: '',
    countingKey: defaultCountingKey(),
    isConfirmed: true,
  };
}

export function eventToFormValues(event: ProceduralTimelineEvent): ProceduralEventFormValues {
  const start = splitDateAndTime(event.startDateTime);
  const end = splitDateAndTime(event.endDateTime);
  return {
    name: event.name,
    description: event.description ?? '',
    timeZone: event.timeZone ?? '',
    startDate: start.date,
    startTime: start.time,
    endDate: end.date,
    endTime: end.time,
    anchorEventId: event.anchorEventId ?? '',
    deltaDays: event.deltaInterval?.days !== undefined ? String(event.deltaInterval.days) : '',
    countingKey: mapDeltaTypeToCounting(event.deltaIntervalType, event.jurisdiction),
    isConfirmed: event.isConfirmed,
  };
}

interface BuildInputOptions {
  /** Require a start date (create mode). Editing may leave the date TBD. */
  requireStartDate: boolean;
  /** Require a time zone (create mode). */
  requireTimeZone: boolean;
}

export interface ProceduralEventForm {
  values: ProceduralEventFormValues;
  setName: (v: string) => void;
  setDescription: (v: string) => void;
  setTimeZone: (v: string) => void;
  setStartDate: (v: Date | null) => void;
  setStartTime: (v: string) => void;
  setEndDate: (v: Date | null) => void;
  setEndTime: (v: string) => void;
  /** Clears the delta / counting selection when the anchor is removed. */
  setAnchorEventId: (v: string) => void;
  setDeltaDays: (v: string) => void;
  setCountingKey: (v: CountingKey) => void;
  setIsConfirmed: (v: boolean) => void;
  anchorOptions: { value: string; label: string }[];
  timezoneOptions: { value: string; label: string }[];
  isCalcOpen: boolean;
  setIsCalcOpen: (v: boolean) => void;
  /** True once the start date/time differs from the value the form opened with. */
  startDateChanged: boolean;
  /** True once any field differs from the value the form opened with. */
  isDirty: boolean;
  /** Validate and build the shared payload, or toast and return null. */
  buildInput: (options: BuildInputOptions) => CreateProceduralEventInput | null;
}

interface UseProceduralEventFormArgs {
  /** Re-initialise from `initialValues` whenever this goes true (dialog opens). */
  open: boolean;
  initialValues: ProceduralEventFormValues;
  /** Distinct per subject (event id / suggestion id / 'new') to re-seed on switch. */
  resetKey: string;
  eventsById: Record<string, ProceduralTimelineEvent>;
  /** Event being edited — excluded (with its descendants) from anchor options. */
  currentEventId: string | null;
}

export function useProceduralEventForm({
  open,
  initialValues,
  resetKey,
  eventsById,
  currentEventId,
}: UseProceduralEventFormArgs): ProceduralEventForm {
  const [name, setName] = useState(initialValues.name);
  const [description, setDescription] = useState(initialValues.description);
  const [timeZone, setTimeZone] = useState(initialValues.timeZone);
  const [startDate, setStartDate] = useState<Date | null>(initialValues.startDate);
  const [startTime, setStartTime] = useState(initialValues.startTime);
  const [endDate, setEndDate] = useState<Date | null>(initialValues.endDate);
  const [endTime, setEndTime] = useState(initialValues.endTime);
  const [anchorEventId, setAnchorEventIdState] = useState(initialValues.anchorEventId);
  const [deltaDays, setDeltaDays] = useState(initialValues.deltaDays);
  const [countingKey, setCountingKey] = useState<CountingKey>(initialValues.countingKey);
  const [isConfirmed, setIsConfirmed] = useState(initialValues.isConfirmed);
  const [isCalcOpen, setIsCalcOpen] = useState(false);

  // Read latest initial values inside the reset effect without making it a dep
  // (the parent recomputes the object every render).
  const initialRef = useRef(initialValues);
  initialRef.current = initialValues;

  useEffect(() => {
    if (!open) return;
    const v = initialRef.current;
    setName(v.name);
    setDescription(v.description);
    setTimeZone(v.timeZone);
    setStartDate(v.startDate);
    setStartTime(v.startTime);
    setEndDate(v.endDate);
    setEndTime(v.endTime);
    setAnchorEventIdState(v.anchorEventId);
    setDeltaDays(v.deltaDays);
    setCountingKey(v.countingKey);
    setIsConfirmed(v.isConfirmed);
    setIsCalcOpen(false);
  }, [open, resetKey]);

  const setAnchorEventId = (v: string) => {
    setAnchorEventIdState(v);
    if (v === '') {
      setDeltaDays('');
      setCountingKey(defaultCountingKey());
    }
  };

  const cycleCreatingIds = useMemo(() => {
    // Exclude the edited event's descendants, which would form a cycle.
    if (currentEventId === null) return new Set<string>();
    const descendants = new Set<string>();
    const queue = [currentEventId];
    while (queue.length) {
      const current = queue.shift()!;
      for (const e of Object.values(eventsById)) {
        if (e.anchorEventId === current && !descendants.has(e.id)) {
          descendants.add(e.id);
          queue.push(e.id);
        }
      }
    }
    return descendants;
  }, [eventsById, currentEventId]);

  const anchorOptions = useMemo(
    () =>
      Object.values(eventsById)
        .filter((e) => e.id !== currentEventId && !cycleCreatingIds.has(e.id))
        .map((e) => ({ value: e.id, label: e.name })),
    [eventsById, currentEventId, cycleCreatingIds],
  );

  const timezoneOptions = useMemo(() => getTimeZoneOptions(timeZone), [timeZone]);

  const initialStartIso = useMemo(() => {
    const v = initialRef.current;
    return v.startDate !== null ? combineDateAndTime(v.startDate, v.startTime) : undefined;
  }, [resetKey]);
  const currentStartIso = startDate !== null ? combineDateAndTime(startDate, startTime) : undefined;
  const startDateChanged = currentStartIso !== initialStartIso;

  // Whether any field differs from the values the form opened with. Dates are
  // compared as combined civil ISO strings so a same-instant re-pick doesn't
  // register as a change; name is trimmed to match how it's submitted.
  const currentValues: ProceduralEventFormValues = {
    name,
    description,
    timeZone,
    startDate,
    startTime,
    endDate,
    endTime,
    anchorEventId,
    deltaDays,
    countingKey,
    isConfirmed,
  };
  const isDirty = !formValuesEqual(currentValues, initialRef.current);

  const buildInput = ({ requireStartDate, requireTimeZone }: BuildInputOptions): CreateProceduralEventInput | null => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error('Name is required.');
      return null;
    }
    if (requireStartDate && startDate === null) {
      toast.error('Start date is required.');
      return null;
    }
    if (requireTimeZone && timeZone === '') {
      toast.error('Time zone is required.');
      return null;
    }

    const anchored = anchorEventId !== '';
    const parsedDays = parseDaysOrNull(deltaDays);
    if (anchored && parsedDays === null) {
      toast.error('Enter how many days after the dependent event this falls.');
      return null;
    }
    if (parsedDays !== null && !anchored) {
      toast.error('Pick an event for this to depend upon.');
      return null;
    }

    const startIso = startDate !== null ? combineDateAndTime(startDate, startTime) : undefined;
    const endIso = endDate !== null ? combineDateAndTime(endDate, endTime) : undefined;
    if (startIso !== undefined && endIso !== undefined && new Date(endIso) < new Date(startIso)) {
      toast.error('End must be on or after start.');
      return null;
    }

    return {
      name: trimmedName,
      description: description,
      timeZone: timeZone,
      ...(startIso !== undefined ? { startDateTime: startIso } : {}),
      ...(endIso !== undefined ? { endDateTime: endIso } : {}),
      ...(anchored
        ? {
            anchorEventId,
            deltaInterval: { days: parsedDays! },
            ...mapCountingToEvent(countingKey),
          }
        : {}),
      isConfirmed,
    };
  };

  return {
    values: {
      name,
      description,
      timeZone,
      startDate,
      startTime,
      endDate,
      endTime,
      anchorEventId,
      deltaDays,
      countingKey,
      isConfirmed,
    },
    setName,
    setDescription,
    setTimeZone,
    setStartDate,
    setStartTime,
    setEndDate,
    setEndTime,
    setAnchorEventId,
    setDeltaDays,
    setCountingKey,
    setIsConfirmed,
    anchorOptions,
    timezoneOptions,
    isCalcOpen,
    setIsCalcOpen,
    startDateChanged,
    isDirty,
    buildInput,
  };
}

/** A civil date+time as a comparable ISO string (or undefined when unset). */
function civilKey(date: Date | null, time: string): string | undefined {
  return date !== null ? combineDateAndTime(date, time) : undefined;
}

/** Structural equality of two form states, used to detect unsaved changes. */
function formValuesEqual(a: ProceduralEventFormValues, b: ProceduralEventFormValues): boolean {
  return (
    a.name.trim() === b.name.trim() &&
    a.description === b.description &&
    a.timeZone === b.timeZone &&
    civilKey(a.startDate, a.startTime) === civilKey(b.startDate, b.startTime) &&
    civilKey(a.endDate, a.endTime) === civilKey(b.endDate, b.endTime) &&
    a.anchorEventId === b.anchorEventId &&
    a.deltaDays === b.deltaDays &&
    a.countingKey === b.countingKey &&
    a.isConfirmed === b.isConfirmed
  );
}

interface ProceduralEventFieldsProps {
  form: ProceduralEventForm;
  matterId: string;
  /** Edit mode makes date/zone optional + clearable (create requires them). */
  isEdit: boolean;
  eventsById: Record<string, ProceduralTimelineEvent>;
}

/**
 * The shared field body for both the create/edit dialog and the review editor.
 * Owns no submission logic — the parent reads `form.buildInput()`.
 */
export function ProceduralEventFields({ form, matterId, isEdit, eventsById }: ProceduralEventFieldsProps) {
  const { values } = form;
  return (
    <>
      <div>
        <Label htmlFor="event-name">Event name</Label>
        <Input
          id="event-name"
          name="event-name"
          value={values.name}
          onChange={(e) => form.setName(e.target.value)}
          placeholder="e.g. Defence due"
          autoComplete="off"
          data-1p-ignore
          data-lpignore="true"
        />
      </div>

      <div>
        <Label htmlFor="event-description">Description (optional)</Label>
        <Textarea
          id="event-description"
          value={values.description}
          onChange={(e) => form.setDescription(e.target.value)}
          placeholder="Optional context shown alongside the event."
          rows={3}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Start date{isEdit ? ' (optional)' : ''}</Label>
          <CivilDatePicker
            value={values.startDate}
            onChange={form.setStartDate}
            clearable={isEdit}
            placeholder="Pick a date"
          />
        </div>
        <div>
          <Label htmlFor="event-start-time">Start time (optional)</Label>
          <Input
            id="event-start-time"
            type="time"
            value={values.startTime}
            onChange={(e) => form.setStartTime(e.target.value)}
            disabled={values.startDate === null}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>End date (optional)</Label>
          <CivilDatePicker
            value={values.endDate}
            onChange={form.setEndDate}
            defaultMonth={values.startDate}
            clearable
            placeholder="Pick a date"
          />
        </div>
        <div>
          <Label htmlFor="event-end-time">End time (optional)</Label>
          <Input
            id="event-end-time"
            type="time"
            value={values.endTime}
            onChange={(e) => form.setEndTime(e.target.value)}
            disabled={values.endDate === null}
          />
        </div>
      </div>

      <div>
        <Label>Time zone{isEdit ? ' (optional)' : ''}</Label>
        <SearchableCombobox
          options={form.timezoneOptions}
          value={values.timeZone}
          onChange={form.setTimeZone}
          placeholder="Pick a time zone"
          searchPlaceholder="Search time zones"
          emptyText="No matching zones."
          triggerClassName="w-full justify-between font-normal"
          containerClassName="w-full [&>span]:block"
          clearable={isEdit}
        />
      </div>

      <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
        <p className="mb-2 text-xs font-medium text-gray-700">Dependent event (optional)</p>
        <p className="mb-3 text-xs text-gray-500">
          Make this event's date depend upon another event. Pick the event it follows from, how many days later (use a
          negative number for earlier) it falls, and whether weekends and holidays count.
        </p>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Depends upon</Label>
            <SearchableCombobox
              options={form.anchorOptions}
              value={values.anchorEventId}
              onChange={form.setAnchorEventId}
              placeholder="None"
              searchPlaceholder="Search events"
              emptyText="No matching events."
              triggerClassName="w-full justify-between font-normal"
              containerClassName="w-full [&>span]:block"
            />
          </div>
          <div>
            <Label htmlFor="event-delta-days">Days after</Label>
            <Input
              id="event-delta-days"
              type="number"
              min={-DAYS_LIMIT}
              max={DAYS_LIMIT}
              value={values.deltaDays}
              onChange={(e) => form.setDeltaDays(e.target.value)}
              onWheel={(e) => e.currentTarget.blur()}
              disabled={values.anchorEventId === ''}
              placeholder="e.g. 14"
            />
          </div>
          <div>
            <div className="mb-2 flex items-center gap-1.5">
              <Label className="mb-0">Counting method</Label>
              <CountingReference side="top" align="end" />
            </div>
            <Select
              value={values.countingKey}
              onValueChange={(v) => form.setCountingKey(v as CountingKey)}
              disabled={values.anchorEventId === ''}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUNTING_OPTIONS.map((option) => (
                  <SelectItem key={option.key} value={option.key}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button
          type="button"
          variant="primary"
          onClick={() => form.setIsCalcOpen(true)}
          disabled={values.anchorEventId === ''}
          className="mt-3 w-full"
        >
          <Icon className="lucide--calculator" />
          Calculate date
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <Switch id="event-confirmed" checked={values.isConfirmed} onCheckedChange={form.setIsConfirmed} />
        <div className="space-y-1">
          <label htmlFor="event-confirmed" className="block cursor-pointer text-sm font-medium text-gray-800">
            Event is confirmed
          </label>
          <p className="text-xs text-gray-500">
            Confirmed dates are finalised. Toggle confirmation if the date depends on an earlier decision in the
            timeline or is likely to change.
          </p>
        </div>
      </div>

      <Dialog open={form.isCalcOpen} onOpenChange={form.setIsCalcOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Timeline calculator</DialogTitle>
            <DialogDescription>Calculate the deadline from the dependent event's date.</DialogDescription>
          </DialogHeader>
          {values.anchorEventId !== '' && (
            <EmbeddedCalculator
              matterId={matterId}
              sourceDate={toDateOrNull(eventsById[values.anchorEventId]?.startDateTime)}
              days={parseDaysOrZero(values.deltaDays)}
              counting={values.countingKey}
              onApply={(date) => {
                form.setStartDate(instantToCivilUtc(date));
                form.setIsCalcOpen(false);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export function parseDaysOrNull(value: string): number | null {
  if (value === '' || value === '-') return null;
  const n = parseInt(value, 10);
  if (!Number.isFinite(n)) return null;
  if (n > DAYS_LIMIT) return DAYS_LIMIT;
  if (n < -DAYS_LIMIT) return -DAYS_LIMIT;
  return n;
}

function parseDaysOrZero(value: string): number {
  return parseDaysOrNull(value) ?? 0;
}

/** `event.startDateTime` is typed as `Date` but arrives as an ISO string over the wire. */
export function toDateOrNull(value: Date | string | null | undefined): Date | null {
  if (value == null) return null;
  return value instanceof Date ? value : new Date(value);
}
